import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from "react";
import { useTable, useSortBy, useFilters } from "react-table";
import { useVirtual } from "react-virtual";

// ICONS
import { ReactComponent as FavoriteIcon } from "../../build/images/icons/favoritetwo.svg";
import { ReactComponent as FavoriteEnableIcon } from "../../build/images/star.svg";
import { ReactComponent as LeadIcon } from "../../build/images/icons/leadmail.svg";
import { ReactComponent as SmsIcon } from "../../build/images/smsgrain.svg";

// MUI
import TablePagination from "@mui/material/TablePagination";
import { createTheme, ThemeProvider, useTheme } from "@mui/material/styles";
import { ptBR } from "@mui/material/locale";
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";

// COMPONENTS
import OrderButton from "../order-button/OrderButton";
import TimeCounterInternal from "../../containers/TimeCounterInternalDash";
import TimeCounterDashVirtual from "../../containers/TimeCounterDashVirtual";
import { Tooltippy, PopoverDashboard } from "../../actions/Tippy";

// STYLES
import "./table-virtual.css";

const TableVirtual = ({
  className = "",
  data,
  columns,
  fixedHeader,
  filtersActive,
  emptyState = false,
  setEmptyState = () => null,
  controlledTableState,
  screen = "",
  initialState,
  rowShadowLine = false,
  classDivWrapperTable = null,
  renderHandleActions,
  renderRowHoverActions,
  ...others
}) => {
  const defaultData = useMemo(() => [], []);

  const {
    headerGroups,
    rows,
    prepareRow,
    visibleColumns,
    state: { sortBy },
  } = useTable(
    {
      columns,
      data: data || defaultData,
      autoResetSortBy: false,
      useControlledState: (state) => ({ ...state, ...controlledTableState }),
      initialState: initialState,
    },
    useFilters,
    useSortBy,
  );

  const theme = useTheme();

  // STATES
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [containerWidth, setContainerWidth] = useState(0);

  // REFS
  const parentRef = useRef();

  // PAGINAÇÃO
  const countPagination =
    parseInt(rows.length / rowsPerPage) +
    (rows.length % rowsPerPage !== 0 ? 1 : 0);

  // DADOS DA PÁGINA
  const currentPageData = useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return rows.slice(start, end);
  }, [rows, page, rowsPerPage]);

  // VIRTUALIZER
  const rowVirtualizer = useVirtual({
    size: currentPageData.length,
    parentRef,
    estimateSize: useCallback(() => 60, []),
    overscan: 5,
  });

  // --- SALVAR SORT NO LOCALSTORAGE ---
  useEffect(() => {
    if (sortBy && sortBy.length > 0) {
      const sortMarked = {
        sort: sortBy[0].id,
        sortDesc: sortBy[0].desc,
      };

      const keyMap = {
        manager: "sortMarkedService",
        manager_colaborators: "sortMarkedColaborators",
        manager_operators: "sortMarkedOperators",
        manager_groupRow: "sortMarkedGroupRow",
        npsTables: "sortDealingRow",
        campaigns: "sortCampaigns",
      };

      if (keyMap[screen]) {
        localStorage.setItem(keyMap[screen], JSON.stringify(sortMarked));
      }
    }
  }, [sortBy, screen]);

  // --- CÁLCULO DA LARGURA TOTAL ---
  const totalColumnsWidth = useMemo(() => {
    return visibleColumns.reduce((total, column) => {
      if (column.display) {
        const width = column.width || 120;
        return total + width;
      }

      return total;
    }, 0);
  }, [visibleColumns]);

  // --- MONITOR DE REDIMENSIONAMENTO ---
  useEffect(() => {
    const updateWidth = () => {
      if (parentRef.current) {
        setContainerWidth(parentRef.current.clientWidth);
      }
    };

    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  // HANDLERS
  const handleChangePage = (event, newPage) => setPage(newPage - 1);

  const handleChangeRowsPerPage = (event) => {
    const val = parseInt(event.target.value, 10);
    setRowsPerPage(val);
    setPage(0);

    const storageMap = {
      manager_colaborators: "rows_perpage_colaborators",
      manager: "rows_perpage_services",
      manager_operators: "rows_perpage_operators",
      manager_groupRow: "rows_perpage_groupRow",
      npsTables: "rows_perpage_npsTables",
      campaigns: "rows_perpage_campaigns",
    };
    if (storageMap[screen]) localStorage.setItem(storageMap[screen], val);
  };

  function defaultLabelDisplayedRows({ from, to, count }) {
    return `${from}–${to} de ${count !== -1 ? count : `mais de ${to}`} itens`;
  }

  const themeWithLocale = React.useMemo(
    () => createTheme(theme, ptBR),
    [ptBR, theme],
  );
  const displayHeader = (head) => head.display !== false;
  const displayBody = (body) => body.column.display !== false;

  // --- RENDER CONTENT (SEM OPTIONAL CHAINING) ---
  function renderCellContent(cell, row, index) {
    if (cell.value === undefined || cell.value === null) {
      if (
        !["actions_favorite", "actions_icon"].includes(cell.column.typeCell)
      ) {
        return "";
      }
    }

    const type = cell.column.typeCell;
    let className = "cell-gray-text";
    let colorTag = "";
    if (cell.value && cell.value.config && cell.value.config.colorTag) {
      colorTag = `tag-chip --color-${cell.value.config.colorTag}`;
    }
    if (cell.value.config && cell.value.config.className) {
      className = cell.value.config.className;
    }

    switch (type) {
      case "timer":
        if (!cell.value.data && cell.value.data !== 0) return "-";
        return (
          <span className={className}>
            <TimeCounterDashVirtual
              key={`${row.id}-${cell.column.id}`}
              horario={cell.value.data}
            />
          </span>
        );

      case "popover":
        const userData = cell.value.data;
        if (!userData) return "";

        return (
          <span className={className}>
            <PopoverDashboard
              key={`${row.id}-${cell.column.id}`}
              trigger="click"
              boundary="window"
              placement="top"
              multiple={true}
              interactive={true}
              content={
                renderHandleActions ? renderHandleActions(userData) : null
              }
            >
              <div style={{ cursor: "pointer" }}>{userData.label}</div>
            </PopoverDashboard>
          </span>
        );

      case "tooltip":
        const textData = cell.value.data;
        return (
          <Tooltippy key={`${row.id}-${cell.column.id}`} content={textData}>
            <span className="cell-blue-text">
              {textData && textData.length > 13
                ? textData.substr(0, 10) + "..."
                : textData}
            </span>
          </Tooltippy>
        );

      case "actions_favorite":
        const isFavorite =
          row.values[cell.column.id] &&
          row.values[cell.column.id].config &&
          row.values[cell.column.id].config.favorite;
        return (
          <div className="cell-content__container">
            <input
              type="checkbox"
              id={"cell" + index}
              className="cell-content__container-checkbox"
            />
            <div className="cell-content__container-icon">
              {isFavorite ? <FavoriteEnableIcon /> : <FavoriteIcon />}
            </div>
          </div>
        );

      case "actions_icon":
        const isRead =
          row.values.actions_primary &&
          row.values.actions_primary.config &&
          row.values.actions_primary.config.read;
        return (
          <div className="cell-content__container">
            <div className="cell-content__container-icon">
              {isRead ? <LeadIcon /> : <SmsIcon />}
            </div>
          </div>
        );

      case "tagChip":
        const tagValue =
          typeof cell.value === "object" && cell.value.data !== undefined
            ? cell.value.data
            : cell.value;

        return (
          <span
            key={`${row.id}-${cell.column.id}`}
            className={className + " " + colorTag}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="8"
              height="8"
              viewBox="0 0 8 8"
              fill="none"
            >
              <circle cx="4" cy="4" r="4" fill="#019F3C" />
            </svg>
            {tagValue}
          </span>
        );

      default:
        const displayValue =
          typeof cell.value === "object" && cell.value.data !== undefined
            ? cell.value.data
            : cell.value;

        return (
          <span key={`${row.id}-${cell.column.id}`} className={className}>
            {displayValue}
          </span>
        );
    }
  }

  function renderCell(row, index) {
    return row.cells.filter(displayBody).map((cell) => {
      let cellClass = "";
      if (cell.value && cell.value.config && cell.value.config.cellColor) {
        cellClass = cell.value.config.cellColor;
      }

      const width = cell.column.width || 120;

      return (
        <div
          {...cell.getCellProps()}
          className={`td ${cellClass}`}
          style={{
            flexGrow: 1,
            flexShrink: 0,
            flexBasis: `${width}px`,
            minWidth: `${width}px`,
            display: "flex",
            alignItems: "center",
            boxSizing: "border-box",
          }}
        >
          <div className="cell-container">
            {renderCellContent(cell, row, index)}
          </div>
        </div>
      );
    });
  }

  // EFFECTS
  useEffect(() => {
    if (!emptyState && rows.length === 0) setEmptyState(true);
    else if (emptyState && rows.length > 0) setEmptyState(false);
  }, [rows.length, emptyState, setEmptyState]);

  useEffect(() => {
    if (parentRef.current) {
      parentRef.current.scrollTop = 0;
      parentRef.current.scrollLeft = 0;
    }
  }, [page]);

  useEffect(() => {
    let loadedRows = 5;
    const storageMap = {
      manager_colaborators: "rows_perpage_colaborators",
      manager: "rows_perpage_services",
      manager_operators: "rows_perpage_operators",
      manager_groupRow: "rows_perpage_groupRow",
      npsTables: "rows_perpage_npsTables",
      campaigns: "rows_perpage_campaigns",
    };
    if (storageMap[screen]) {
      const saved = localStorage.getItem(storageMap[screen]);
      if (saved) setRowsPerPage(parseInt(saved));
    }
  }, [screen]);

  const contentWidth =
    totalColumnsWidth > containerWidth ? `${totalColumnsWidth}px` : "100%";

  return (
    <div
      className={`section-email-list-virtual ${className}`}
      style={{ display: "flex", flexDirection: "column", height: "100%" }}
    >
      <div
        ref={parentRef}
        className={classDivWrapperTable || ""}
        style={{
          flexGrow: 1,
          overflow: "auto",
          display: "block",
          position: "relative",
        }}
      >
        {/* HEADER */}
        <div
          className="thead"
          style={{
            display: "flex",
            minWidth: "100%",
            width: contentWidth,
            position: "sticky",
            top: 0,
            zIndex: 1,
          }}
        >
          {headerGroups.map((headerGroup) => (
            <div
              {...headerGroup.getHeaderGroupProps()}
              style={{ display: "flex", width: "100%" }}
            >
              {headerGroup.headers.filter(displayHeader).map((column) => {
                const width = column.width || 120;
                return (
                  <div
                    {...column.getHeaderProps(
                      column.getSortByToggleProps({ title: column.Hint }),
                    )}
                    className="th"
                    style={{
                      flexGrow: 1,
                      flexShrink: 0,
                      flexBasis: `${width}px`,
                      minWidth: `${width}px`,

                      display: "flex",
                      alignItems: "center",
                      boxSizing: "border-box",
                    }}
                  >
                    <div className="flex-container">
                      <span className="table-header__title">
                        {column.render("Header")}
                      </span>
                      {column.canSort && (
                        <OrderButton
                          direction={
                            column.isSorted &&
                            (column.isSortedDesc ? "desc" : "asc")
                          }
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* BODY */}
        <div
          className="tbody"
          style={{
            minWidth: "100%",
            width: contentWidth,
            height: `${rowVirtualizer.totalSize}px`,
            position: "relative",
          }}
        >
          {rowVirtualizer.virtualItems.map((virtualRow) => {
            const row = currentPageData[virtualRow.index];
            prepareRow(row);

            let isRead = false;
            if (
              row.values.actions_primary &&
              row.values.actions_primary.config &&
              row.values.actions_primary.config.read
            ) {
              isRead = true;
            }

            return (
              <div
                key={row.id}
                ref={virtualRow.measureRef}
                {...row.getRowProps()}
                className={`tr ${isRead ? "read" : "unread"} ${
                  rowShadowLine ? "shadow-line" : ""
                } ${virtualRow.index % 2 !== 0 ? "tr-even" : "tr-odd"}`}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: `${virtualRow.size}px`,
                  transform: `translateY(${virtualRow.start}px)`,
                  display: "flex",
                  // backgroundColor: "#fff",
                }}
              >
                {renderCell(row, virtualRow.index)}
                {renderRowHoverActions && (
                  <div
                    style={{
                      position: "sticky",
                      right: 0,
                      width: 0,
                      height: "100%",
                      display: "flex",
                      justifyContent: "flex-end",
                      zIndex: 2,
                    }}
                  >
                    <div className="row-hover-actions-container">
                      {renderRowHoverActions(row.original)}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* PAGINAÇÃO */}
      <div className="pagination-table">
        <ThemeProvider theme={themeWithLocale}>
          <TablePagination
            rowsPerPageOptions={[5, 10, 15, 25, 50, 100, 500]}
            component="div"
            count={rows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelDisplayedRows={defaultLabelDisplayedRows}
            backIconButtonProps={{ sx: { display: "none" } }}
            nextIconButtonProps={{ sx: { display: "none" } }}
            sx={{ fontFamily: "Inter" }}
          />
        </ThemeProvider>
        <Stack spacing={2}>
          <Pagination
            count={countPagination}
            siblingCount={1}
            color="primary"
            onChange={handleChangePage}
            page={page + 1}
          />
        </Stack>
      </div>
    </div>
  );
};

export default React.memo(TableVirtual);
